import findIndex from 'lodash/findIndex.js';
import sortBy from 'lodash/sortBy.js';

import Model from '@/models/Model.js';
import TimePeriodTemplate from '@/models/TimePeriodTemplate.js';
import moment from '@/plugins/vue-moment.js';

export default class SectionTemplate extends Model {
    static entity = 'section_templates';

    constructor(record) {
        super(record);

        this.type = 'section_template';
    }

    static fields() {
        return {
            id: this.attr(null),
            section_settings_template_id: this.attr(null),
            course_template_id: this.attr(null),
            parent_id: this.attr(null),
            uuid: this.string(''),
            title: this.string(''),
            description: this.string(''),
            is_active: this.boolean(true),
            order: this.number(1),

            ...super.fields(),

            section_settings_template: this.belongsTo('section_settings_templates', 'section_settings_template_id'),
            course_template: this.belongsTo('course_templates', 'course_template_id'),
            time_period_templates: this.hasMany('time_period_templates', 'section_template_id'),
            objective_template: this.morphOne('objectives', 'subject_id', 'subject_type_entity'),
            section_templates: this.hasMany('section_templates', 'parent_id'),
            activity_templates: this.hasMany('activity_templates', 'section_template_id'),
        };
    }

    get scheduled_time() {
        const totals = {
            total_minutes: 0,
            total_break_minutes: 0,
            hours: 0,
            minutes: 0,
            break_hours: 0,
            break_minutes: 0,
        };

        this.time_period_templates.forEach(timePeriodTemplate => {
            totals.total_minutes += timePeriodTemplate.scheduled_time.total_minutes;
            totals.total_break_minutes += timePeriodTemplate.scheduled_time.break_minutes;
        });

        totals.hours = Math.floor(totals.total_minutes / 60);
        totals.minutes = totals.total_minutes - totals.hours * 60;

        totals.break_hours = Math.floor(totals.total_break_minutes / 60);
        totals.break_minutes = totals.total_break_minutes - totals.break_hours * 60;

        return totals;
    }

    get is_week() {
        return this.section_settings_template.type === 'week';
    }

    get is_break() {
        return this.section_settings_template.type === 'break';
    }

    get is_topic() {
        return this.section_settings_template.type === 'topic';
    }

    get display_week_number() {
        // Break weeks occupy their own slot in the timeline but are not instructional
        // weeks, so they must be excluded from week numbering. Counting only week
        // sections keeps the numbering before and after a break consistent with the
        // configured course outline.
        const weekSectionTemplates = sortBy(
            SectionTemplate.query()
                .with('section_settings_template')
                .where('course_template_id', this.course_template_id)
                .whereHas('section_settings_template', query => query.where('type', 'week'))
                .get(),
            'section_settings_template.week_number'
        );

        return findIndex(weekSectionTemplates, sectionTemplate => sectionTemplate.id === this.id) + 1;
    }

    static prospectiveTimeRanges(selectedSectionTemplateIds, times, editing = false) {
        const { timePeriodTemplateId } = times;
        const original = TimePeriodTemplate.find(timePeriodTemplateId);

        return SectionTemplate.query()
            .with('section_settings_template')
            .whereIdIn(selectedSectionTemplateIds)
            .get()
            .map(sectionTemplate => {
                const timeParams = {
                    sectionTemplate,
                    original,
                    editing,
                    times,
                };

                const from = this.calculateTime({ ...timeParams, timeType: 'from' });
                const to = this.calculateTime({ ...timeParams, timeType: 'to' });

                return [from, to, timePeriodTemplateId];
            });
    }

    static calculateTime({ sectionTemplate, original, editing, times, timeType }) {
        const dayKey = `${timeType}Day`;
        const timeKey = `${timeType}Time`;
        const applyDayKey = `apply${timeType.charAt(0).toUpperCase() + timeType.slice(1)}Day`;
        const applyTimeKey = `apply${timeType.charAt(0).toUpperCase() + timeType.slice(1)}Time`;

        const resultTime = moment()
            .startOf('week')
            .add(sectionTemplate.section_settings_template.week_number - 1, 'weeks');

        if (times[applyDayKey] || !editing) {
            resultTime.add(times[dayKey] - 1, 'days');
        } else if (original) {
            resultTime.add(original[`${timeType}_day`], 'days');
        }

        if (times[applyTimeKey] || !editing) {
            resultTime.hour(times[timeKey].getHours()).minute(times[timeKey].getMinutes());
        } else if (original) {
            const [hour, minute] = original[`${timeType}_time`];
            resultTime.hour(hour).minute(minute);
        }

        return resultTime;
    }

    menuItem(parentId, index, nestedItems) {
        return {
            id: this.id,
            parent_id: parentId,
            course_template_id: this.course_template_id,
            prerequisite: this.prerequisite,
            prerequisite_id: this.prerequisite_id,
            title: this.is_topic ? this.title : `Week ${this.display_week_number}`,
            type: 'section_template',
            uuid: this.uuid,
            order: index,
            completed: false,
            course_authorized_role_id: null,
            activity_type: null,
            items: nestedItems,
            points_possible: this.points_possible,
            parent: this.parent,
        };
    }

    static weekNumber(sectionTemplateId) {
        const sectionTemplate = SectionTemplate.query().with('section_settings_template').find(sectionTemplateId);

        return sectionTemplate.section_settings_template.week_number;
    }
}
